import { useCallback, useEffect, useState } from "react";
import { Plus, Link2 } from "lucide-react";
import { Badge } from "../../components/Badge";
import { Button, buttonClass } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { RichText } from "../../components/RichText";
import { Input } from "../../components/Input";
import { Select } from "../../components/Select";
import { useToast } from "../../components/Toast";
import { api } from "../../api/client";
import { useAuth } from "../auth/useAuth";
import { Link } from "react-router-dom";
import type { MeetingNote, MeetingNoteKind, MeetingNoteList, MeetingNoteRow } from "../../api/types";

// 會議紀錄 / 結案報告 / 檢討報告(第 18 輪)。
//
// 業主原話:「每個案件內部的 會議記錄/結案報告/檢討報告/ 同時也要開發一般常會
// 會議紀錄(會議記錄裡面可以TAG系統內任何資訊紀錄當做連結)」。
//
// --------------------------------------------------------------------------
// 三種是同一份資料的三種用途
// --------------------------------------------------------------------------
// 欄位一模一樣,分開列、分開篩就夠了。分三張表要換來三套 TAG、三套權限、
// 三個列表、三份搜尋 —— 而任何一個改動漏掉其中一邊,那一邊不會壞掉,
// 只會安靜地少一個功能。
//
// --------------------------------------------------------------------------
// 誰改得動:作者本人與老闆
// --------------------------------------------------------------------------
// 業務不該改得動別人寫的紀錄 —— 那是一份有署名的文件,改了別人的字卻還
// 掛著別人的名字,比改不動嚴重得多。

const KINDS: [MeetingNoteKind | "", string][] = [
  ["", "全部"],
  ["MEETING", "會議紀錄"],
  ["CLOSING", "結案報告"],
  ["REVIEW", "檢討報告"],
];

/** 種類的色調。**字樣不在這裡** —— 那由後端給。 */
function toneFor(kind: MeetingNoteKind): "neutral" | "accent" | "warn" {
  if (kind === "CLOSING") return "accent";
  if (kind === "REVIEW") return "warn";
  return "neutral";
}

export function MeetingsPage() {
  const [kind, setKind] = useState<MeetingNoteKind | "">("");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<MeetingNoteRow[]>([]);
  const [open, setOpen] = useState<MeetingNote | null>(null);
  const toast = useToast();
  const { user } = useAuth();

  const refresh = useCallback(() => {
    api
      .get<MeetingNoteList>("/meeting-notes", {
        ...(kind ? { kind } : {}),
        ...(q.trim() ? { q: q.trim() } : {}),
      })
      // 形狀要擋 —— 拿到不是預期的東西直接 `.items.map()` 會讓整棵樹白掉。
      .then((got) => setRows(got && Array.isArray(got.items) ? got.items : []))
      .catch(() => setRows([]));
  }, [kind, q]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /** 改得動嗎:**作者本人與老闆** —— 與後端的 `_may_edit()` 同一條規則。 */
  const mayEdit = (row: { created_by: number | null }): boolean =>
    user !== null && (row.created_by === user.id || user.role === "OWNER");

  const remove = async (row: MeetingNoteRow) => {
    try {
      await api.del(`/meeting-notes/${row.id}`);
      // **講清楚它救得回來。** 第 24 輪起刪除是丟進回收桶(業主 2026-09-20),
      // 不講的話使用者會以為那篇沒了,而回收桶那一頁他根本不知道要去。
      toast.push("已丟進回收桶,七天內救得回來", "success");
      refresh();
    } catch (err) {
      toast.push(err instanceof Error ? err.message : "刪不掉", "error");
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="m-0 text-xl font-semibold text-[var(--text)]">會議紀錄</h2>
        {/* **整頁,不是懸浮視窗**(業主 2026-09-17:「新增不要用懸浮視窗
            因為可能會議可能會很長」)。見 `MeetingNotePage` 的檔頭。 */}
        <Link to="/meetings/new" className={buttonClass("primary", "md", "no-underline")}>
          <Plus aria-hidden="true" size={16} />
          寫一份
        </Link>
      </div>

      {/* **同一列裡的每一格都走 `Input` / `Select`。**
          手刻的 `<label><span>` 標籤用的是 `text-xs`,而元件用 `text-sm
          font-medium` —— 兩者一高一低,同一列就對不齊(業主 2026-09-17 的截圖)。

          **說明文字也不放在格子裡**:有說明的那一格比別人高,整列跟著歪。
          移到整列下面。 */}
      <div className="flex flex-wrap items-end gap-3">
        <Select
          label="種類篩選"
          className="w-32"
          value={kind}
          onChange={(e) => setKind(e.target.value as MeetingNoteKind | "")}
          options={KINDS.map(([value, text]) => ({ value, label: text }))}
        />
        <Input
          label="搜尋"
          className="w-64"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      <p className="m-0 -mt-2 text-xs text-[var(--text-3)]">標題、內容與出席者都搜得到。</p>

      {rows.length === 0 ? (
        <EmptyState
          title="還沒有任何紀錄"
          description="開完會寫幾行,三個月後要找「那次講到舞台尺寸的會」才找得到。"
        />
      ) : (
        <ul className="m-0 flex flex-col gap-2 p-0">
          {rows.map((row) => (
            <li
              key={row.id}
              className="list-none rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] px-4 py-3"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  {/* 種類的字樣由後端給 —— 前端只決定顏色。 */}
                  <Badge tone={toneFor(row.kind)}>{row.kind_label}</Badge>
                  {/* 標題連到那一篇的網址 —— 助理回的
                      「[會議紀錄|某某](/meetings/12)」要點得進去。 */}
                  <Link
                    to={`/meetings/${row.id}`}
                    className="text-base font-semibold text-[var(--text)] underline-offset-2 hover:underline"
                  >
                    {row.title}
                  </Link>
                  {row.quote_no ? <Badge tone="neutral">{row.quote_no}</Badge> : null}
                  {row.link_count > 0 ? (
                    <span className="inline-flex items-center gap-1 text-xs text-[var(--text-3)]">
                      <Link2 aria-hidden="true" size={12} />
                      {row.link_count}
                    </span>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[var(--text-3)]">
                    {row.met_on} · {row.author_name}
                  </span>
                  {mayEdit(row) ? (
                    <Link
                      to={`/meetings/${row.id}`}
                      className={buttonClass("secondary", "sm", "no-underline")}
                    >
                      修改
                    </Link>
                  ) : (
                    // 改不動的那一篇**不給連結** —— 點得下去卻做不了事更惱人。
                    // 那是一份有署名的文件:改了別人的字卻還掛著別人的名字,
                    // 比改不動嚴重得多。
                    <Button
                      size="sm"
                      disabled
                      title="這份是別人寫的,只有他本人或老闆改得動"
                    >
                      修改
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={!mayEdit(row)}
                    title={mayEdit(row) ? undefined : "這份是別人寫的,只有他本人或老闆刪得掉"}
                    onClick={() => void remove(row)}
                  >
                    刪除
                  </Button>
                </div>
              </div>
              {row.excerpt ? (
                <p className="m-0 mt-1 truncate text-sm text-[var(--text-2)]">{row.excerpt}</p>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {/* 讀的時候整篇攤開,連結列在下面。 */}
      {open ? (
        <section
          aria-label={`紀錄:${open.title}`}
          className="rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] px-4 py-3"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="m-0 text-lg font-semibold text-[var(--text)]">{open.title}</h3>
            <Button size="sm" onClick={() => setOpen(null)}>
              收起來
            </Button>
          </div>
          <p className="m-0 mt-1 text-sm text-[var(--text-3)]">
            {open.kind_label} · {open.met_on} · {open.author_name}
            {open.attendees ? ` · 出席:${open.attendees}` : ""}
          </p>
          {/* 換行照換行顯示 —— 會議紀錄就是一條一條的。 */}
          {/* 內容是 HTML(第 22 輪)。**消毒在後端** —— 見 components/RichText.tsx。 */}
          <div className="mt-2">
            <RichText html={open.body} />
          </div>
          {open.links.length > 0 ? (
            <ul aria-label="連結" className="m-0 mt-2 flex flex-wrap gap-2 p-0">
              {open.links.map((link) => (
                <li key={link.id} className="list-none">
                  {link.is_dangling ? (
                    // **連結留著,只是點不進去。** 那場會議真的開過 ——
                    // 「那天我們討論的是 NB-2026-0007」不會因為那張單被刪掉就沒發生。
                    <Badge tone="neutral">{`${link.target_label}(已刪除)`}</Badge>
                  ) : (
                    <Badge tone="accent">{`${link.target_type_label}:${link.target_label}`}</Badge>
                  )}
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}

    </div>
  );
}
